import type { ThemeProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import {
  excludeBreakpointVariables,
  generateInlineThemeVariables,
  generateTailwindVariables,
  generateVariantComponents,
  TAILWIND_CSS_CLASS,
} from './themeHelpers';

/**
 * The canvas runtime (@tailwindcss/browser v4, see useTailwind.ts) compiles every
 * `text/tailwindcss` style as one block: a single unknown utility in an `@apply` rule fails
 * the whole block and strips every theme utility from the canvas (PostHog project 381053,
 * 362+ occurrences — missing font tokens like `font-avantt` and literal `none` values).
 *
 * Page.tsx injects from inside a browser bundle, so the exact server-side compiler
 * (`@tailwindcss/node`, used by buildPageUpdates/phases/validate.ts and buildTailwindCss.ts)
 * cannot run here. This module is the browser-safe pre-injection check: it flags only the
 * utilities that are *provably* unresolvable (literal CSS keywords, theme-token families with
 * a missing token), never guessing about core utilities it can't enumerate.
 *
 * Where a real compile error exists, its message is parsed with UNKNOWN_UTILITY_PATTERN —
 * the same Tailwind v4 error contract the buildPageUpdates pipeline reads.
 */

// Mirrors apps/champaign/src/modules/creativeAgents/operations/tools/v2/buildPageUpdates/phases/validate.ts
export const UNKNOWN_UTILITY_PATTERN = /Cannot apply unknown utility class `([^`]+)`/;

export const parseUnknownUtilityClasses = (errorText: string): string[] =>
  [...errorText.matchAll(new RegExp(UNKNOWN_UTILITY_PATTERN.source, 'g'))].flatMap((match) =>
    match[1] ? [match[1]] : [],
  );

// Values may be absent: generateTailwindVariables returns a Partial record over the theme
// variable names.
type ThemeVariableMap = Readonly<Record<string, string | undefined>>;

const APPLY_RULE_PATTERN = /@apply\s+([^;}{]+)/g;

// CSS keywords are never Tailwind utilities; emitting one as a bare token fails the block.
const CSS_KEYWORD_TOKENS = new Set(['none', 'inherit', 'initial', 'unset', 'revert']);

const CORE_FONT_FAMILIES = new Set(['sans', 'serif', 'mono']);
const CORE_FONT_WEIGHTS = new Set([
  'thin',
  'extralight',
  'light',
  'normal',
  'medium',
  'semibold',
  'bold',
  'extrabold',
  'black',
]);
const CORE_FONT_SIZES = new Set([
  'xs',
  'sm',
  'base',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
  '8xl',
  '9xl',
]);
const CORE_COLOR_KEYWORDS = new Set(['transparent', 'current', 'inherit', 'black', 'white']);
const CORE_SHADOWS = new Set(['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', 'inner', 'none']);

// Mirrors TAILWIND_COLOR_HUES in apps/champaign/src/pages/buildTailwindCss.ts — the core
// palette hues Tailwind ships, referenced as `<hue>-<shade>`.
const CORE_HUES = new Set([
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
  'slate',
  'gray',
  'zinc',
  'neutral',
  'stone',
]);
const CORE_SHADES = new Set(['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950']);

// Mirrors COLOR_UTILITY_PATTERN in buildPageUpdates/phases/validate.ts.
const COLOR_UTILITY_PATTERN =
  /(?:^|:)!?(?:accent|bg|border|caret|decoration|divide|fill|from|outline|ring|shadow|stroke|text|to|via)-/;
const BORDER_STYLE_FAMILIES = new Set(['border', 'divide', 'outline', 'decoration', 'ring']);
const BORDER_STYLE_VALUES = new Set(['solid', 'dashed', 'dotted', 'double', 'hidden', 'inset', 'none']);
// border-t-4, divide-y-2, ring-offset-2 are widths, not colors — direction/offset segments
// precede a numeric width.
const BORDER_WIDTH_VALUE_PATTERN = /^(?:(?:t|r|b|l|x|y|s|e|offset)-)?\d+(?:\.\d+)?$/;
const WIDTH_VALUE_PATTERN = /^\d+(?:\.\d+)?$/;

const isKnownColorValue = (family: string, value: string, variables: ThemeVariableMap): boolean => {
  const bare = value.split('/')[0] ?? '';
  if (bare.startsWith('[') || bare.startsWith('(')) return true;
  if (variables[`--color-${bare}`] !== undefined) return true;
  if (CORE_COLOR_KEYWORDS.has(bare)) return true;
  if (WIDTH_VALUE_PATTERN.test(bare)) return true;
  if (BORDER_STYLE_FAMILIES.has(family) && BORDER_STYLE_VALUES.has(bare)) return true;
  if (BORDER_STYLE_FAMILIES.has(family) && BORDER_WIDTH_VALUE_PATTERN.test(bare)) return true;
  // bg-none, fill-none, shadow-none are real utilities; text-none is not a utility at all.
  if (bare === 'none') return family !== 'text';
  const [hue, shade] = bare.split('-');
  return hue !== undefined && CORE_HUES.has(hue) && CORE_SHADES.has(shade ?? '');
};

const isKnownUtility = (utility: string, variables: ThemeVariableMap): boolean => {
  if (CSS_KEYWORD_TOKENS.has(utility)) return false;

  const dashIndex = utility.indexOf('-');
  if (dashIndex < 1) return true; // bare or negative utilities are not provable statically
  const family = utility.slice(0, dashIndex);
  const value = utility.slice(dashIndex + 1);
  if (value.startsWith('[') || value.startsWith('(')) return true; // arbitrary/functional values always compile

  switch (family) {
    case 'font':
      return (
        CORE_FONT_WEIGHTS.has(value) || CORE_FONT_FAMILIES.has(value) || variables[`--font-${value}`] !== undefined
      );
    case 'shadow':
      return (
        CORE_SHADOWS.has(value) ||
        variables[`--shadow-${value}`] !== undefined ||
        isKnownColorValue(family, value, variables)
      );
    case 'text':
      return CORE_FONT_SIZES.has(value) || isKnownColorValue(family, value, variables);
    default:
      return COLOR_UTILITY_PATTERN.test(utility) ? isKnownColorValue(family, value, variables) : true;
  }
};

/**
 * Utilities referenced by `@apply` rules in the given CSS that the canvas runtime cannot
 * resolve. Variant prefixes (hover:, md:) and `!` are stripped; only provable failures are
 * reported.
 */
export const findUnknownApplyUtilities = (css: string, themeVariables: ThemeVariableMap): string[] => {
  const unknown = new Set<string>();
  for (const match of css.matchAll(APPLY_RULE_PATTERN)) {
    const applyBody = match[1];
    if (!applyBody) continue;
    for (const rawToken of applyBody.split(/\s+/)) {
      if (!rawToken) continue;
      const token = rawToken.replace(/^!/, '');
      const utility = (token.split(':').pop() ?? token).replace(/^!/, '');
      if (utility && !isKnownUtility(utility, themeVariables)) unknown.add(utility);
    }
  }
  return [...unknown];
};

export type ThemeComponentsValidation = { ok: true } | { ok: false; offendingClasses: string[]; reason: string };

export const validateThemeComponents = (
  componentsCss: string,
  themeVariables: ThemeVariableMap,
): ThemeComponentsValidation => {
  const offendingClasses = findUnknownApplyUtilities(componentsCss, themeVariables);
  if (offendingClasses.length === 0) return { ok: true };
  return {
    ok: false,
    offendingClasses,
    reason: `Cannot apply unknown utility class${offendingClasses.length > 1 ? 'es' : ''}: ${offendingClasses.join(', ')}`,
  };
};

// Minimal valid components block for a first render with no last-known-good block available:
// text-dark always resolves because the theme schema emits a --color-dark default.
export const FALLBACK_THEME_COMPONENTS = `@layer components {\n    .${TAILWIND_CSS_CLASS} { @apply text-dark; }\n  }`;

export type PageThemeCss = {
  inlineThemeVars: string[];
  scopedVariables: ReturnType<typeof excludeBreakpointVariables>;
  components: string;
};

export type ThemeBlockFailure = {
  offendingClasses: string[];
  reason: string;
};

// Fallback content generated from an empty theme, so the fallback block's @apply text-dark
// always resolves with matching theme variables even when generation threw on malformed data.
const buildFallbackThemeContent = (): PageThemeCss => {
  try {
    const tailwindVars = generateTailwindVariables({} as ThemeProps);
    return {
      inlineThemeVars: generateInlineThemeVariables(tailwindVars),
      scopedVariables: excludeBreakpointVariables(tailwindVars),
      components: FALLBACK_THEME_COMPONENTS,
    };
  } catch {
    return { inlineThemeVars: [], scopedVariables: {}, components: FALLBACK_THEME_COMPONENTS };
  }
};

/**
 * Generate the theme CSS for a page and validate it before it reaches the canvas. On an
 * invalid or ungeneratable block, the last-known-good content is kept (or the fallback on a
 * first render) instead of letting the canvas runtime drop every theme utility. Never
 * throws: generation errors (malformed theme data) surface as `failure`.
 */
export const resolveThemeBlockCss = (
  themeProps: ThemeProps,
  lastKnownGood: PageThemeCss | null,
): { content: PageThemeCss; failure: ThemeBlockFailure | null } => {
  try {
    const tailwindVars = generateTailwindVariables(themeProps);
    const inlineThemeVars = generateInlineThemeVariables(tailwindVars);
    const scopedVariables = excludeBreakpointVariables(tailwindVars);
    const components = generateVariantComponents(themeProps);

    const validation = validateThemeComponents(components, tailwindVars);
    if (validation.ok) {
      return { content: { inlineThemeVars, scopedVariables, components }, failure: null };
    }

    return {
      content: lastKnownGood ?? { inlineThemeVars, scopedVariables, components: FALLBACK_THEME_COMPONENTS },
      failure: { offendingClasses: validation.offendingClasses, reason: validation.reason },
    };
  } catch (error) {
    return {
      content: lastKnownGood ?? buildFallbackThemeContent(),
      failure: {
        offendingClasses: [],
        reason: error instanceof Error ? error.message : 'Theme CSS generation failed',
      },
    };
  }
};
